import React from 'react'
import { connect } from 'react-redux'
import filter from 'lodash/filter'
import find from 'lodash/find'
import sortBy from 'lodash/sortBy'
import StaticQuestionSlide from '../../slides/StaticQuestionSlide'
import Icon from '../../misc/Icons'
import { formatDate } from '../../../utils/formatDate'

const CARD_WIDTH = 152 // 140px thumb + 12px gap

// One card in the horizontal carousel — represents a single set inside the pack.
function PackSetCard({ set }) {
  const questionCount = Array.isArray(set.questions) ? set.questions.length : 0
  const firstQuestion = questionCount > 0 ? set.questions[0] : null
  const canRenderThumb = !!(firstQuestion && firstQuestion.bodyHtml)
  return (
    <div className='searchPreviewPack-card'>
      <div className='searchPreviewPack-card-thumb'>
        {canRenderThumb ? (
          <StaticQuestionSlide
            question={firstQuestion}
            stage='ask'
            mode='view'
            displayWidth={138}
            previewCard
          />
        ) : (
          <div className='searchPreviewPack-card-thumb-placeholder'>
            <Icon name='search-set' questionCount={questionCount} />
          </div>
        )}
      </div>
      <div className='searchPreviewPack-card-title' title={set.name}>
        {set.name || 'Untitled Set'}
      </div>
      <div className='searchPreviewPack-card-meta'>
        {`${questionCount} question${questionCount === 1 ? '' : 's'}`}
      </div>
    </div>
  )
}

function SearchPreviewPack({
  hit, sets, folderCount, storePack,
}) {
  const pack = storePack || hit
  const scrollerRef = React.useRef(null)

  const scrollByCards = React.useCallback((dir) => {
    if (!scrollerRef.current) return
    scrollerRef.current.scrollBy({ left: dir * CARD_WIDTH, behavior: 'smooth' })
  }, [])

  const onWheel = (e) => {
    if (!scrollerRef.current) return
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
      e.preventDefault()
      scrollerRef.current.scrollLeft += e.deltaY
    }
  }

  // Arrow-key navigation: only hijack when the caret is at the matching edge
  // of the search input so we don't fight normal text editing.
  React.useEffect(() => {
    const handler = (e) => {
      if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return
      const input = e.target
      if (!input || input.id !== 'topNavSearchBar') return
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
      const caretAtStart = input.selectionStart === 0
      const caretAtEnd = input.selectionStart === (input.value || '').length
      if (e.key === 'ArrowLeft' && caretAtStart) {
        e.preventDefault()
        e.stopPropagation()
        scrollByCards(-1)
      } else if (e.key === 'ArrowRight' && caretAtEnd) {
        e.preventDefault()
        e.stopPropagation()
        scrollByCards(1)
      }
    }
    document.addEventListener('keydown', handler, true)
    return () => document.removeEventListener('keydown', handler, true)
  }, [scrollByCards])

  const setCount = sets.length
  const editedAt = pack && pack.clientModified
  const hasSets = setCount > 0

  return (
    <div className='searchPreviewPane-body searchPreviewPack'>
      <div className='searchPreviewPane-header'>
        <div className='searchPreviewPane-header-icon'>
          <Icon name='search-pack' />
        </div>
        <div className='searchPreviewPane-header-text'>
          <div className='searchPreviewPane-header-title'>
            {pack && pack.name}
          </div>
          <div className='searchPreviewPane-header-meta'>
            <span>{`${setCount} set${setCount === 1 ? '' : 's'}`}</span>
            <span>{`${folderCount} folder${folderCount === 1 ? '' : 's'}`}</span>
          </div>
        </div>
      </div>

      {hasSets ? (
        <div className='searchPreviewPack-carouselWrapper'>
          <button
            type='button'
            className='searchPreviewPack-navBtn searchPreviewPack-navBtn--prev'
            onClick={() => scrollByCards(-1)}
            tabIndex={-1}
            aria-label='Scroll left'
          >
            <Icon name='chevron-left' />
          </button>
          <div className='searchPreviewPack-carousel' ref={scrollerRef} onWheel={onWheel}>
            {sets.map((set) => (
              <PackSetCard key={set.id} set={set} />
            ))}
          </div>
          <button
            type='button'
            className='searchPreviewPack-navBtn searchPreviewPack-navBtn--next'
            onClick={() => scrollByCards(1)}
            tabIndex={-1}
            aria-label='Scroll right'
          >
            <Icon name='chevron-right' />
          </button>
        </div>
      ) : (
        <div className='searchPreviewPack-empty'>
          <Icon name='search-pack' />
          <span>
            {pack && pack.description ? pack.description : 'No sets are loaded locally for this pack yet.'}
          </span>
        </div>
      )}
      <div className='searchPreviewPane-meta'>
        <span className='searchPreviewPane-meta-kind'>Pack</span>
        {editedAt && (
          <span className='searchPreviewPane-meta-edited'>
            {'Edited '}
            {formatDate(editedAt)}
          </span>
        )}
      </div>
    </div>
  )
}

function mapStateToProps(state, ownProps) {
  const id = ownProps.hit && ownProps.hit.id
  if (!id) return { sets: [], folderCount: 0, storePack: null }
  const setsInPack = sortBy(
    filter(state.sets, (s) => s && s.repo === id && !s.archived),
    (s) => -((s.clientModified && new Date(s.clientModified).getTime()) || 0),
  )
  const folderCount = filter(state.folders, (f) => f && f.repo === id && !f.archived).length
  return {
    sets: setsInPack,
    folderCount,
    storePack: find(state.repos, { id }),
  }
}

export default connect(mapStateToProps)(SearchPreviewPack)
